import React, { useEffect, useState } from "react";
import { logError } from "../lib/log";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { ArrowRight, RotateCcw } from "lucide-react";
import { QuizOptions } from "../components/QuizOptions";

function TopicPicker() {
  const [topics, setTopics] = useState([]);
  useEffect(() => {
    api.get("/topics").then((r) => setTopics(r.data.topics)).catch((e) => logError("Topics load failed", e));
  }, []);
  return (
    <div className="space-y-8" data-testid="quiz-picker">
      <div>
        <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">Practice Quiz</h1>
        <p className="mt-2 text-sm text-slate-400">Pick a topic to start.</p>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {topics.map((t) => (
          <Link
            key={t.id}
            to={`/quiz/${t.id}`}
            data-testid={`quiz-pick-${t.id}`}
            className="group flex items-center justify-between rounded-md border border-white/10 bg-black/40 p-5 transition hover:border-cyan-400/40 hover:shadow-[0_0_20px_rgba(0,229,255,0.15)]"
          >
            <div>
              <div className="font-display text-lg font-semibold text-white">{t.title}</div>
              <div className="text-xs text-slate-500">{t.difficulty}</div>
            </div>
            <ArrowRight size={16} className="text-slate-400 group-hover:translate-x-1 group-hover:text-cyan-300 transition-transform" />
          </Link>
        ))}
      </div>
    </div>
  );
}

function QuizPlay({ topicId }) {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [topicTitle, setTopicTitle] = useState("");
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setQuestions([]);
    setAnswers({});
    setResult(null);
    Promise.all([api.get(`/quiz/${topicId}`), api.get(`/topics/${topicId}`)])
      .then(([q, t]) => {
        setQuestions(q.data.questions);
        setTopicTitle(t.data.title);
      })
      .catch((e) => setErr(apiError(e, "Quiz not found.")));
  }, [topicId]);

  const submit = async () => {
    setLoading(true);
    try {
      const ordered = questions.map((_, i) => (i in answers ? answers[i] : -1));
      const { data } = await api.post(`/quiz/${topicId}/grade`, { answers: ordered });
      setResult(data);
      await api.post("/quiz-attempts", { topic_id: topicId, score: data.score, total: data.total }).catch((e) => logError("Attempt save failed", e));
      // Save wrong answers for later review (best-effort, ignore if unauthenticated)
      data.results.forEach((r, i) => {
        if (!r.is_correct && r.chosen_index !== -1 && r.chosen_index != null) {
          api.post("/me/mistakes", {
            source: "topic-quiz",
            source_id: topicId,
            q: questions[i].q,
            options: questions[i].options,
            correct_index: r.correct_index,
            chosen_index: r.chosen_index,
            explanation: r.explanation,
          }).catch(() => { /* anonymous users have no mistake log */ });
        }
      });
    } catch (e) {
      setErr(apiError(e, "Could not grade the quiz."));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setAnswers({});
    setResult(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (err) return <div className="rounded-md border border-red-400/40 bg-red-500/10 p-4 text-sm text-red-200" data-testid="quiz-error">{err}</div>;
  if (!questions.length) return <div className="text-slate-400">Loading quiz…</div>;

  const allAnswered = questions.every((_, i) => i in answers);

  return (
    <div className="space-y-8" data-testid="quiz-play">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/quiz" className="text-xs uppercase tracking-widest text-slate-400 hover:text-cyan-300" data-testid="back-to-quiz-picker">← All quizzes</Link>
          <h1 className="font-display mt-3 text-3xl font-bold sm:text-4xl">{topicTitle} Quiz</h1>
        </div>
        <div className="font-mono-num text-xs uppercase tracking-widest text-slate-500">
          {Object.keys(answers).length} / {questions.length} answered
        </div>
      </div>

      <div className="space-y-4">
        {questions.map((q, qi) => {
          const r = result?.results[qi];
          return (
            <div key={`${qi}-${q.q}`} className="rounded-md border border-white/10 bg-black/40 p-5" data-testid={`quiz-q-${qi}`}>
              <div className="font-mono-num text-[10px] uppercase tracking-widest text-cyan-300">Q{qi + 1}</div>
              <div className="font-display mt-2 text-lg font-semibold text-white">{q.q}</div>
              <QuizOptions options={q.options} chosen={answers[qi]} correctIndex={r?.correct_index} graded={!!result} onChoose={(oi) => setAnswers({ ...answers, [qi]: oi })} testId={(oi) => `quiz-q-${qi}-opt-${oi}`} />
              {result && (
                <div className="mt-3 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-300" data-testid={`quiz-q-${qi}-explanation`}>
                  {r.explanation}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        {!result ? (
          <button
            onClick={submit}
            disabled={!allAnswered || loading}
            data-testid="submit-quiz-btn"
            className="inline-flex items-center gap-2 rounded-md bg-cyan-500 px-5 py-3 text-sm font-semibold text-black transition-colors hover:bg-cyan-400 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-slate-500"
          >
            Submit answers
          </button>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded-md border border-cyan-400/40 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-200" data-testid="quiz-score">
              You scored <b className="font-mono-num">{result.score}</b> / {result.total}
            </div>
            <button onClick={reset} data-testid="retry-quiz-btn" className="inline-flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-4 py-3 text-sm font-medium hover:bg-white/10">
              <RotateCcw size={14} /> Retry
            </button>
            <button onClick={() => navigate("/quiz")} data-testid="another-quiz-btn" className="inline-flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-4 py-3 text-sm font-medium hover:bg-white/10">
              Another quiz
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Quiz() {
  const { topicId } = useParams();
  if (!topicId) return <TopicPicker />;
  return <QuizPlay topicId={topicId} />;
}
